import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import { ExternalLink, Gift, Info, MapPin, Phone, Tag, User } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { SeoHead, Spinner, CustomerCouponCard } from 'components';
// ASSUMPTION: SEOHead's SITE_URL is imported directly from the component
// file rather than re-exported through the 'components' barrel — confirm
// 'components/SEOHead' actually resolves (matches wherever the file above
// lives) or adjust this path/alias to whatever your barrel re-exports.
import { SITE_URL } from 'components/SeoHead/SeoHead';
import { COUPON_TYPES } from 'coupon-common';
import { actions as businessDetailsActions, duck as businessDetailsDuck } from 'coupon-common/src/modules/businesses/details';
import { actions as followActions } from 'coupon-common/src/modules/businesses/follow';
import { actions as appliedActions } from 'coupon-common/src/modules/customerCoupons/applied';
// Same slice Home/GuestHome fetch into and keep cached across navigation —
// reused here (not re-fetched from scratch) to suggest a few coupons from
// the same city when this coupon can't be shown. See "SUGGESTED COUPONS"
// note near the not-found branch below.
import { actions as allCouponsActions } from 'coupon-common/src/modules/customerCoupons/all';
import { actions, duck as detailsDuck } from 'coupon-common/src/modules/customerCoupons/details';
import { actions as favCouponActions } from 'coupon-common/src/modules/customerCoupons/favourite';
import { dispatchAsAsync } from 'coupon-common/src/utils';

import NoImage from '../../../assets/images/NoCouponImage.webp';
import { RouteNames } from '../../../constants';
import { resolveImageUrl } from '../../../utils/cleanExternalUrls';
import CouponAction from './CouponAction';
import CouponDescription from './CouponDescription';
import styles from './CouponsDetails.module.scss';

const COUPON_DETAIL_INITIAL_STATE = detailsDuck.COUPON_DETAIL_INITIAL_STATE;
const BUSINESS_DETAIL_INITIAL_STATE = businessDetailsDuck.BUSINESS_DETAIL_INITIAL_STATE;

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const GOOGLE_MAPS_LIBRARIES = [];

// Applied-coupon states that count as "currently active/reserved" for the
// Active badge + gradient button — same set UsedCoupons/Favourites use
// for their own reservation-limit checks.
const ACTIVE_APPLIED_STATES = new Set(['reserved', 'accepted', 'waiting']);

// TODO: Remove once the legacy DB lat/lon swap is corrected. All locations
// are in the northern hemisphere, so a negative latitude means the pair was
// stored in reverse order — swap them back. Same check used in
// CouponsMapAll.js / BusinessesDetails.js. 
const normalizeHemisphere = ({ latitude, longitude }) => {
  if (latitude < 0) {
    return { latitude: longitude, longitude: latitude };
  }
  return { latitude, longitude };
};

// Business coordinates are stored as [latitude, longitude] — matches the
// same convention BusinessesDetails already assumes for its own map.
const resolveCoordinate = location => {
  const coords = location?.coordinates;
  if (!Array.isArray(coords) || coords.length < 2) return null;
  const [rawLatitude, rawLongitude] = coords;
  if (!Number.isFinite(rawLatitude) || !Number.isFinite(rawLongitude)) return null;
  const { latitude, longitude } = normalizeHemisphere({ latitude: rawLatitude, longitude: rawLongitude });
  return { lat: latitude, lng: longitude };
};

// Returns the first non-whitespace character of a business name, upper-
// cased, for the fallback logo avatar. Falls back to '?' if the name is
// missing/empty so the avatar never renders blank.
const getBusinessInitial = name => {
  const trimmed = (name || '').trim();
  return trimmed ? trimmed[0].toUpperCase() : '?';
};

/**
 * Web port of Coupons/CouponsDetails, redesigned to match the new
 * "die-cut ticket" mock: a hero card (image + deal info side-by-side, with
 * the favourite/redeem actions inline), a two-column lower section
 * (business/contact panel + description/terms panels), and a "Get
 * Direction" map card at the bottom.
 *
 * ACTIVE RESERVATION: reads state.customerCoupons.applied (same slice
 * Favourites.js/UsedCoupons.js already use for their own reservation
 * checks) to find whether THIS coupon has a live reserved/accepted/
 * waiting record for the current user, and if so, until when
 * (expiry_time). Drives:
 *   - the "Active" badge, shown in the same row as the title, pinned to
 *     the right edge of the content pane
 *   - the `isReserved` prop passed to CouponAction, which needs to apply
 *     the gradient(90deg, #ef4444, #7f1d1d) button treatment while
 *     reservedUntil hasn't passed — CouponAction.js itself needs to read
 *     this prop and style accordingly; not done here since that file
 *     wasn't available.
 *
 * Per direction, the following mobile-only features were stripped rather
 * than stubbed, since their backing redux modules don't exist in the web
 * store: "Report this coupon", the affiliate "Deals" tab (rakuten/affiliate
 * feed), and the "More from this business" coupon list driven by
 * businesses/coupons + businesses/affiliateCoupons. Favouriting, business
 * info, directions/call, and reserve/redeem all work against the real
 * customerCoupons.* + businesses.details modules.
 *
 * IMAGE / LOGO FALLBACKS:
 *   - Hero image: if the coupon has no resolvable image, NoCouponImage.webp
 *     is shown instead of a broken <img>.
 *   - Business logo: if the business has no logo, a circular avatar showing
 *     the first letter of the business name is rendered in place of the
 *     <img>, styled to match .businessLogo's sizing (see
 *     .businessLogoFallback in the stylesheet).
 *
 * HOURS-TO-USE CHIP: reservedHours only reflects a real usage window for
 * coupons that live in our own reservation system. Affiliate/flyer coupons
 * don't go through that flow, so the chip is hidden whenever
 * isAffiliateOrFlyer is true, even if reservedHours happens to be set.
 *
 * FOLLOW LOGIN GATE (new): toggleFollowHandler now checks `authorized`
 * first and opens loginPromptVisible instead of dispatching when the
 * visitor is a guest. This mirrors the Activate button's existing gate in
 * CouponAction — that component owns its own copy of the same modal
 * because the heart button lives inside it, but the Follow button is
 * rendered here in the business panel, outside CouponAction's tree, so a
 * second small copy of the modal is rendered here rather than trying to
 * share state across the two components.
 *
 * SEO (new): SEOHead renders title/description/OG/Twitter/JSON-LD for this
 * coupon, built from the same `data`/`businessData`/`dealAmount`/
 * `categoryName`/`resolvedHeroImage` values already computed below — no new
 * fetches. Only rendered in the successful-render return; the error/loading
 * early returns above it fall back to SEOHead's site-wide defaults, since
 * those states are transient and not worth indexing distinctly.
 */
const CouponsDetails = () => {
  const { id: idParam } = useParams();
  const id = idParam ? Number(idParam) : null;
  const location = useLocation();
  const passedBusinessLocation = location.state?.businessLocation ?? null;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [optimisticFavourite, setOptimisticFavourite] = useState(null);
  const [termsOpen, setTermsOpen] = useState(false);
  const [heroImageFailed, setHeroImageFailed] = useState(false);
  const [loginPromptVisible, setLoginPromptVisible] = useState(false);

  const couponDetailsState = useSelector(state => state.customerCoupons.details);
  const couponState = (id && couponDetailsState?.[id]) || COUPON_DETAIL_INITIAL_STATE;
  const { fetching, fetched, data, error } = couponState;
  const { image_url: imageUrls } = data || {};

  const customerProfile = useSelector(state => state.customerProfile);
  const allFavourite = useSelector(state => state.customerCoupons.favourite);
  const followState = useSelector(state => state.businesses.follow);
  const appliedState = useSelector(state => state.customerCoupons.applied);

  // ─── Suggested coupons (shown when this coupon can't be found) ───────
  // Same slice + shape Home/GuestHome already read from (`items` keyed by
  // id, `result.results` an ordered id list) — whatever's cached there
  // from browsing is reused as-is rather than issuing a dedicated
  // "similar coupons" request.
  const customerCouponsAllState = useSelector(state => state.customerCoupons.all || {});
  const { items: allCouponItems, result: allCouponResult, fetching: allCouponsFetching } = customerCouponsAllState;
  const activeCityId = useSelector(state => (state.cities && state.cities.activeCityId) || null);

  // Same auth flag CouponAction reads for its own gating — kept here too
  // since the Follow button's gate lives in this component.
  const authorized = useSelector(state => !!(state.auth && state.auth.authorized));

  const businessDetailsState = useSelector(state => state.businesses.details);
  const businessId = data?.business?.id;
  const businessDetailState = (businessId && businessDetailsState?.[businessId]) || BUSINESS_DETAIL_INITIAL_STATE;
  const fullBusinessDetails = businessDetailState?.data;

  const {
    business: businessData,
    type,
    amount_off: amountOff,
    percentage_off: percentageOff,
    points_back: pointsBack,
    is_exclusive_followers: isExclusiveFollowers,
    is_exclusive_paid: isExclusivePaid,
    user_limit: userLimit,
    reserved_hours: reservedHours,
    is_affiliate_or_flyer: isAffiliateOrFlyer,
    payment_by_points: doPaymentByPoints,
    category,
  } = data || {};

  // ─── Fetch coupon detail ──────────────────────────────────────────────
  const getCouponDetail = useCallback(() => {
    if (id) dispatch(actions.getDetail(id));
  }, [dispatch, id]);

  useEffect(() => {
    getCouponDetail();
  }, [getCouponDetail]);

  // ─── Fetch business detail (for address/phone) + follow list + favourites + applied ──
  useEffect(() => {
    if (businessId) {
      dispatch(businessDetailsActions.getDetail(businessId));
    }
  }, [businessId, dispatch]);

  useEffect(() => {
    dispatch(favCouponActions.getAllFavouriteList());
    dispatch(followActions.getFollowedBusinessesRequest());
    dispatch(appliedActions.getApplyCouponList());
  }, [dispatch]);

  // ─── Favourite state (optimistic) ────────────────────────────────────
  const favouritesFetched = !!allFavourite?.fetched;
  const isFavouriteFromRedux = useMemo(() => {
    if (!favouritesFetched || !data?.id) return false;
    return (allFavourite?.data?.results || []).some(f => f.id === data.id);
  }, [favouritesFetched, allFavourite?.data?.results, data?.id]);

  const isFavourite = optimisticFavourite !== null ? optimisticFavourite : isFavouriteFromRedux;

  const toggleFavouriteHandler = useCallback(() => {
    if (!data?.id) return;
    setOptimisticFavourite(!isFavourite);
    const action = isFavourite ? favCouponActions.removeFavourite(data.id) : favCouponActions.markAsFavourite(data.id);
    dispatchAsAsync(action, dispatch).then(() => {
      dispatchAsAsync(favCouponActions.getAllFavouriteList(), dispatch).then(() => {
        setOptimisticFavourite(null);
      });
    });
  }, [dispatch, data?.id, isFavourite]);

  // ─── Follow state ──────────────────────────────────────────────────────
  const followRecord = useMemo(
    () => (followState?.data?.results || []).find(b => b.business_id === businessId),
    [followState?.data?.results, businessId],
  );
  const isBusinessFollowed = !!followRecord;

  const toggleFollowHandler = useCallback(() => {
    if (!authorized) {
      setLoginPromptVisible(true);
      return;
    }
    if (!businessId) return;
    if (isBusinessFollowed) {
      dispatch(
        followActions.unfollowBusinessRequest({
          id: followRecord?.id,
          business_id: businessId,
          is_affiliate_or_flyer: !!isAffiliateOrFlyer,
        }),
      );
    } else {
      dispatch(
        followActions.followBusinessRequest({
          business_id: businessId,
          is_affiliate_or_flyer: !!isAffiliateOrFlyer,
        }),
      );
    }
  }, [authorized, dispatch, businessId, isBusinessFollowed, followRecord, isAffiliateOrFlyer]);

  // ─── Active reservation lookup (drives Active badge + reserved button) ───
  const activeReservation = useMemo(() => {
    if (!data?.id) return null;
    const results = appliedState?.data?.results || [];
    const now = new Date();
    return (
      results.find(item => {
        if (item.coupon !== data.id && item?.coupon_details?.id !== data.id) return false;
        if (!ACTIVE_APPLIED_STATES.has(item.state)) return false;
        if (item.expiry_time && new Date(item.expiry_time) <= now) return false;
        return true;
      }) || null
    );
  }, [appliedState?.data?.results, data?.id]);

  const isReserved = !!activeReservation;
  const reservedUntil = activeReservation?.expiry_time || null;

  // ─── Derived display values ───────────────────────────────────────────
  let dealAmount;
  if (type === COUPON_TYPES.AMOUNT_OFF) {
    dealAmount = `$${amountOff} OFF`;
  } else if (type === COUPON_TYPES.PERCENTAGE_OFF) {
    dealAmount = `${Math.round(percentageOff)}% OFF`;
  } else if (type === COUPON_TYPES.POINTS_BACK) {
    dealAmount = `${pointsBack} Points Back`;
  }

  const categoryName = category?.name || (typeof category === 'string' ? category : null);

  const isExclusiveLocked = !isBusinessFollowed && isExclusiveFollowers;
  const isPaidLocked = isExclusivePaid && !customerProfile?.data?.has_active_subscription;

  const affiliateLogo = isAffiliateOrFlyer ? data?.affiliate_logo || businessData?.logo : businessData?.logo;
  const resolvedBusinessLogo = isAffiliateOrFlyer ? affiliateLogo : businessData?.logo;
  const businessInitial = getBusinessInitial(businessData?.name);

  const resolvedHeroImage = resolveImageUrl(imageUrls?.[0]?.image_url) || NoImage;
  const hasRealHeroImage = resolvedHeroImage !== NoImage && !heroImageFailed;

  useEffect(() => {
    setHeroImageFailed(false);
  }, [imageUrls]);

  const coordinate = passedBusinessLocation
    ? resolveCoordinate(passedBusinessLocation)
    : resolveCoordinate(fullBusinessDetails?.location);

  const { isLoaded: isMapLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // ─── Navigation helpers ───────────────────────────────────────────────
  const goToBusinessDetails = bizId => {
    if (!bizId) return;
    if (isAffiliateOrFlyer) {
      navigate(`${RouteNames.AFFILIATE_BUSINESS_DETAILS}/${bizId}`);
    } else {
      navigate(`${RouteNames.BUSINESSES_DETAILS}/${bizId}`);
    }
  };

  const openMapOnLocation = () => {
    if (!coordinate) return;
    navigate(RouteNames.COUPONS_MAPS_ALL, {
      state: { dest: { latitude: coordinate.lat, longitude: coordinate.lng } },
    });
  };

  const handleOpenExternalDirections = () => {
    if (!coordinate) return;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${coordinate.lat},${coordinate.lng}&travelmode=driving`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openPhoneNumber = phone => {
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  };

  // ─── "Coupon unavailable" state ────────────────────────────────────────
  // Covers a bad/stale link: a non-numeric or missing :id (idParam ?
  // Number(idParam) : null above resolves to NaN or null, both falsy), or
  // a numeric id the API errors on (duck sets `error`).
  // NOTE: deliberately NOT also checking `fetched && !data` here — that
  // heuristic assumed a specific success-shape for customerCoupons/details
  // (`fetched: true` implies `data` is populated) that isn't confirmed
  // against the actual duck. If that assumption is wrong, this branch
  // could fire for perfectly valid coupons. Revisit once the duck's
  // shape is confirmed.
  const isCouponUnavailable = !id || !!error;

  // ─── Suggested coupons (shown alongside the unavailable message) ──────
  // Pulled straight from the cached customerCoupons/all list — the same
  // one Home/GuestHome populate — rather than a dedicated "similar
  // coupons" request. If nothing's cached yet (e.g. a visitor lands
  // directly on a dead link with no prior browsing), fetch one page for
  // the active city as a fallback — but only once, via the ref below, so
  // a shape mismatch (e.g. results ending up empty) can't turn this into
  // a repeated dispatch loop.
  const suggestedCoupons = useMemo(() => {
    const ids = allCouponResult?.results || [];
    return ids
      .map(rid => allCouponItems?.[rid])
      .filter(item => item && item.id !== id)
      .slice(0, 4);
  }, [allCouponResult, allCouponItems, id]);

  const hasFetchedSuggestions = useRef(false);
  useEffect(() => {
    if (!isCouponUnavailable) return;
    if (hasFetchedSuggestions.current) return;
    if (suggestedCoupons.length > 0 || allCouponsFetching) return;
    hasFetchedSuggestions.current = true;
    dispatch(allCouponsActions.getAllList({ page: 1, ...(activeCityId ? { city: activeCityId } : {}) }));
  }, [isCouponUnavailable, suggestedCoupons.length, allCouponsFetching, activeCityId, dispatch]);

  // Lightweight favourite check/toggle for the suggested cards — separate
  // from isFavourite/toggleFavouriteHandler above, which only ever track
  // the single coupon this page was loaded for.
  const checkSuggestedFavourite = useCallback(
    item => (allFavourite?.data?.results || []).some(f => f.id === item.id),
    [allFavourite?.data?.results],
  );
  const toggleSuggestedFavourite = useCallback(
    item => {
      if (!authorized) {
        setLoginPromptVisible(true);
        return;
      }
      const action = checkSuggestedFavourite(item) ? favCouponActions.removeFavourite(item.id) : favCouponActions.markAsFavourite(item.id);
      dispatchAsAsync(action, dispatch).then(() => {
        dispatch(favCouponActions.getAllFavouriteList());
      });
    },
    [authorized, checkSuggestedFavourite, dispatch],
  );

  const address1 = fullBusinessDetails?.street || '';
  const address2 = `${fullBusinessDetails?.city?.name || ''}${fullBusinessDetails?.postal_code ? `, ${fullBusinessDetails.postal_code}` : ''}`;
  const addressLine = [address1, address2].filter(Boolean).join(', ') || fullBusinessDetails?.city?.name || '';

  // ─── SEO ────────────────────────────────────────────────────────────
  // ASSUMPTION: couponPath assumes RouteNames.COUPONS_DETAILS is the
  // public-facing coupon route prefix (matches goToBusinessDetails/
  // goToDetails-style navigate calls elsewhere) — confirm it resolves to
  // something crawlable, not a route that requires auth to render.
  const couponPath = `${RouteNames.COUPONS_DETAILS}/${id}`;
  const seoImage = hasRealHeroImage ? resolvedHeroImage : undefined;
  const seoTitle = data?.title
  ? `${data.title}${businessData?.name ? ` | ${businessData.name} Coupon` : ''}`
  : undefined;

// Strip any markup/newlines and keep it snippet-length (~160 chars).
const rawCouponDescription = (data?.description || '')
  .replace(/<[^>]*>/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();
const trimmedCouponDescription =
  rawCouponDescription.length > 160
    ? `${rawCouponDescription.slice(0, 157).trimEnd()}...`
    : rawCouponDescription;

const seoDescription = data
  ? trimmedCouponDescription ||
    `${dealAmount ? `${dealAmount} ` : ''}at ${businessData?.name || 'a local business'}${
      categoryName ? ` — ${categoryName}` : ''
    }. Redeem this deal on My Couponbook.`
  : undefined;
  // const seoDescription = data
  //   ? `${dealAmount ? `${dealAmount} ` : ''}at ${businessData?.name || 'a local business'}${
  //       categoryName ? ` — ${categoryName}` : ''
  //     }. Redeem this deal on My Couponbook.`
  //   : undefined;
  // Seller enrichment for structured data: address/geo/telephone all come
  // from data this component already fetches and renders (fullBusinessDetails
  // contact card + the map's `coordinate`) — no new requests needed.
  // ASSUMPTION: fullBusinessDetails has no country/region field currently
  // read anywhere in this component (addressLine above only ever uses
  // street/city/postal_code), so addressRegion/addressCountry are omitted
  // rather than guessed — add them here if the API response actually
  // includes them under different field names.
  // TODO: openingHoursSpecification and sameAs (social links) aren't
  // fetched by this component at all — businessDetailsActions.getDetail
  // would need to return hours/social fields, and they'd need to be
  // destructured from fullBusinessDetails above, before they can be added
  // here. Left out rather than fabricated.
  const sellerAddress = addressLine
    ? {
        '@type': 'PostalAddress',
        streetAddress: fullBusinessDetails?.street || undefined,
        addressLocality: fullBusinessDetails?.city?.name || undefined,
        postalCode: fullBusinessDetails?.postal_code || undefined,
      }
    : undefined;

  const sellerGeo = coordinate
    ? {
        '@type': 'GeoCoordinates',
        latitude: coordinate.lat,
        longitude: coordinate.lng,
      }
    : undefined;

  const structuredData = data
    ? {
        '@context': 'https://schema.org',
        '@type': 'Offer',
        name: data.title,
        description: data.terms_and_conditions || seoDescription,
        url: `${SITE_URL}${couponPath}`,
        image: seoImage,
        category: categoryName || undefined,
        seller: businessData?.name
          ? {
              '@type': 'LocalBusiness',
              name: businessData.name,
              address: sellerAddress,
              geo: sellerGeo,
              telephone: fullBusinessDetails?.phone || undefined,
            }
          : undefined,
      }
    : undefined;

  const loginPromptModal = loginPromptVisible && (
    <div className={styles.modalOverlay} onClick={() => setLoginPromptVisible(false)}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <h2 className={styles.modalTitle}>Log in to continue</h2>
        <p className={styles.modalMessage}>Create a free account (or log in) to save favourites and follow businesses.</p>
        <div className={styles.modalButtons}>
          <button
            type="button"
            className={styles.modalBtnYes}
            onClick={() => {
              setLoginPromptVisible(false);
              navigate(RouteNames.SIGN_IN);
            }}
          >
            Login
          </button>
        </div>
      </div>
    </div>
  );

  if (isCouponUnavailable) {
    return (
      <div className={styles.page}>
        <div className={styles.wrap}>
          <div className={styles.notFoundWrap}>
            <p className={styles.notFoundText}>This coupon is no longer available, sorry for the inconvenience.</p>
          </div>

          {suggestedCoupons.length > 0 && (
            <div className={styles.suggestedSection}>
              <h2 className={styles.suggestedTitle}>You might also like</h2>
              <div className={styles.suggestedGrid}>
                {suggestedCoupons.map(item => (
                  <CustomerCouponCard
                    key={item.id}
                    data={item}
                    onPress={() => navigate(`${RouteNames.COUPONS_DETAILS}/${item.id}`)}
                    isFavourite={checkSuggestedFavourite(item)}
                    goToBusinessDetails={() => goToBusinessDetails(item.business?.id)}
                    toggleFavourite={() => toggleSuggestedFavourite(item)}
                    isGuest={!authorized}
                    onGuestAction={() => setLoginPromptVisible(true)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        {loginPromptModal}
      </div>
    );
  }

  if (fetching && !fetched) {
    return (
      <div className={styles.page}>
        <div className={styles.wrap} style={{ display: 'flex', justifyContent: 'center', paddingTop: '4rem' }}>
          <Spinner show size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        image={seoImage}
        path={couponPath}
        type="product"
        structuredData={structuredData}
      />
      <div className={styles.wrap}>
        {/* ─── Hero card: image + deal info ─── */}
        <div className={styles.heroCard}>
          <div className={styles.heroImagePane}>
            <img
              src={resolvedHeroImage}
              alt={data?.title || 'Coupon'}
              className={isAffiliateOrFlyer && hasRealHeroImage ? `${styles.heroImage} ${styles.heroImageContain}` : styles.heroImage}
              onError={e => {
                // Guard against broken/expired resolved URLs at request time
                // too, not just when the field is empty up front. Also flip
                // hasRealHeroImage off so isAffiliateOrFlyer coupons drop
                // the 'contain' treatment and the NoImage fallback renders
                // with cover, filling the square instead of letterboxing.
                if (e.currentTarget.src !== NoImage) {
                  e.currentTarget.src = NoImage;
                  setHeroImageFailed(true);
                }
              }}
            />
            <div className={styles.heroBadgeRow}>
              {categoryName && <span className={styles.badgePillWhite}>{categoryName}</span>}
              {isExclusivePaid && <span className={styles.badgePillGold}>Premium</span>}
              {isExclusiveFollowers && <span className={styles.badgePillGold}>Exclusive</span>}
            </div>
          </div>

          <div className={styles.heroContentPane}>
            <div className={styles.heroTopGroup}>
              {/* Title + Active badge share a row — badge pins to the
                  right edge at the same vertical level as the title. */}
              <div className={styles.heroTitleRow}>
                <h1 className={styles.heroTitle}>{data?.title}</h1>

                {isReserved && (
                  <span className={styles.activeBadge}>
                    <span className={styles.activeDot} />
                    Active
                  </span>
                )}
              </div>

              {dealAmount && <div className={styles.heroDealAmount}>{dealAmount}</div>}

              <div className={styles.chipRow}>
                {reservedHours && !isAffiliateOrFlyer && (
                  <div className={styles.chip}>
                    <span className={styles.chipIconBg}>
                      <Gift size={18} />
                    </span>
                    <span className={styles.chipTextCol}>
                      <span className={styles.chipValue}>{reservedHours}</span>
                      <span className={styles.chipLabel}>Hours to Use</span>
                    </span>
                  </div>
                )}
                {categoryName && (
                  <div className={styles.chip}>
                    <span className={styles.chipIconBg}>
                      <Tag size={18} />
                    </span>
                    <span className={styles.chipTextCol}>
                      <span className={styles.chipValue}>{categoryName}</span>
                      <span className={styles.chipLabel}>Category</span>
                    </span>
                  </div>
                )}
                {userLimit && (
                  <div className={styles.chip}>
                    <span className={styles.chipIconBg}>
                      <User size={18} />
                    </span>
                    <span className={styles.chipTextCol}>
                      <span className={styles.chipValue}>{userLimit}</span>
                      <span className={styles.chipLabel}>Redemptions Left</span>
                    </span>
                  </div>
                )}
              </div>
            </div>

            <CouponAction
              data={data || {}}
              dispatch={dispatch}
              gotoGetDirection={openMapOnLocation}
              locked={isExclusiveLocked}
              paywalled={isPaidLocked}
              isAffiliate={isAffiliateOrFlyer}
              affiliateLink={data?.affiliate_link || ''}
              isFavourite={isFavourite}
              favouritesFetched={favouritesFetched}
              onToggleFavourite={toggleFavouriteHandler}
              // CouponAction needs to read these to apply the gradient
              // (90deg, #ef4444, #7f1d1d) treatment to its primary button
              // while isReserved is true.
              isReserved={isReserved}
              reservedUntil={reservedUntil}
            />
          </div>
        </div>

        {/* ─── Lower two-column section ─── */}
        <div className={styles.lowerGrid}>
          {/* Business / contact panel */}
          <div className={`${styles.panel} ${styles.businessPanel}`}>
            <div className={styles.businessPanelHead}>
              {resolvedBusinessLogo ? (
                <img
                  src={resolvedBusinessLogo}
                  alt={businessData?.name || ''}
                  className={styles.businessLogo}
                  onClick={() => goToBusinessDetails(businessData?.id)}
                />
              ) : (
                <div
                  className={styles.businessLogoFallback}
                  onClick={() => goToBusinessDetails(businessData?.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      goToBusinessDetails(businessData?.id);
                    }
                  }}
                  aria-label={businessData?.name || 'Business logo'}
                >
                  {businessInitial}
                </div>
              )}
              <button type="button" className={styles.businessPanelName} onClick={() => goToBusinessDetails(businessData?.id)}>
                {businessData?.name}
              </button>
            </div>

            <div className={styles.contactCard}>
              {fullBusinessDetails?.owner_name && (
                <div className={styles.contactRow}>
                  <User size={16} />
                  <span>{fullBusinessDetails.owner_name}</span>
                </div>
              )}
              {fullBusinessDetails?.phone && (
                <button type="button" className={styles.contactRow} onClick={() => openPhoneNumber(fullBusinessDetails.phone)}>
                  <Phone size={16} />
                  <span>{fullBusinessDetails.phone}</span>
                </button>
              )}
              {addressLine && (
                <button type="button" className={styles.contactRow} onClick={openMapOnLocation}>
                  <MapPin size={16} />
                  <span>{addressLine}</span>
                </button>
              )}
            </div>

            <button
              type="button"
              className={isBusinessFollowed ? styles.followBtn : `${styles.followBtn} ${styles.notFollowing}`}
              onClick={toggleFollowHandler}
            >
              {isBusinessFollowed ? 'Following' : 'Follow'}
            </button>
          </div>

          {/* Description + Terms */}
          <div className={styles.infoStack}>
            <div className={`${styles.panel} ${styles.descriptionPanel}`}>
              <CouponDescription data={data || {}} />

              {doPaymentByPoints && (
                <p className={styles.infoCardText}>
                  Purchasable with points — if you have enough points in your wallet, you can pay for this
                  coupon with your points instead.
                </p>
              )}
            </div>

            <div className={`${styles.panel} ${styles.termsPanel}`}>
              <div
                className={styles.termsHead}
                role="button"
                tabIndex={0}
                onClick={() => setTermsOpen(open => !open)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setTermsOpen(open => !open);
                  }
                }}
                aria-expanded={termsOpen}
              >
                <h3 className={styles.infoCardTitle}>Terms &amp; Conditions</h3>
                <span className={styles.termsToggleBtn} aria-hidden="true">
                  <Info size={20} />
                </span>
              </div>
              {termsOpen && (
                <p className={styles.infoCardText}>
                  {isAffiliateOrFlyer
                    ? `My Couponbook may show deals, coupons, and offers provided by third-party affiliate networks and merchants. These deals are shown "as provided" to us and may change, expire, or be removed at any time. We don't create, maintain, or guarantee the accuracy, availability, or terms of any deal — always check the final details on the merchant's site before buying.\n\nSome links in the app are affiliate links. If you tap a deal and make a purchase, My Couponbook may earn a commission at no extra cost to you.`
                    : data?.terms_and_conditions || 'No terms and conditions provided for this coupon.'}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ─── Get Direction ─── */}
        {coordinate && (
          <div className={styles.directionCard}>
            <div className={styles.directionHead}>
              <MapPin size={18} className={styles.directionIcon} />
              <h2 className={styles.directionTitle}>Get Direction</h2>
              {/* <button type="button" className={styles.directionAction} onClick={handleOpenExternalDirections} title="Open in Google Maps">
                <ExternalLink size={13} className={styles.directionIcon} />
              </button> */}
            </div>

            <div className={styles.mapFrame}>
              {isMapLoaded && GOOGLE_MAPS_API_KEY ? (
                <GoogleMap
                  center={coordinate}
                  zoom={14}
                  options={{ disableDefaultUI: true, gestureHandling: 'cooperative' }}
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  onClick={openMapOnLocation}
                >
                  <Marker position={coordinate} onClick={openMapOnLocation} />
                </GoogleMap>
              ) : (
                <button type="button" className={styles.mapFallback} onClick={openMapOnLocation}>
                  <MapPin size={18} />
                  &nbsp;Tap to view on the map
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {loginPromptModal}
    </div>
  );
};

export default CouponsDetails;